/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
'use client';

import { doTranslation } from '@/app/i18n/client';
import { useMediaQuery } from '@mui/material';
import {default as Image} from '@/components/global/images/resized-cdn-image';
import { useParams } from 'next/navigation';
import React, { useEffect, useRef } from 'react';

import ButtonTertiary from '@/components/global/buttons/button-tertiary';
import Select from '@/components/global/forms/select';
import Tooltip from '@/components/global/tooltip/tooltip';

import circleInfo from '@/public/assets/images/global/booking-widget/circle-info.svg';
import circleInfoDark from '@/public/assets/images/global/bus-station/circle-info-dark.svg';
import toggleOff from '@/public/assets/images/global/booking-widget/toggle-off.svg';
import toggleOn from '@/public/assets/images/global/booking-widget/toggle-on.svg';

import styles from '@/components/global/passengers/passengers.module.scss';
import classNames from 'classnames';

interface Props {
  adultCount: number;
  childCount: number;
  isTripSearch?: boolean;
  passengerOptions: boolean | false;
  seniorCount: number;
  setAdultCount: (s: number) => void;
  setChildCount: (s: number) => void;
  setPassengerOptions: (b: boolean) => void;
  setSeniorCount: (s: number) => void;
  setWheelchair: (b: boolean) => void;
  setWheelchairCount: (s: number) => void;
  wheelchair: boolean;
  wheelchairCount: number;
}

const Passengers = (props: Props): React.JSX.Element => {
  const { lng } = useParams() as { lng: string };
  const adultRef = useRef(null);
  const seniorRef = useRef(null);
  const childRef = useRef(null);
  const passengersRef = useRef(null);
  const ref = useRef<HTMLDivElement>(null);
  const observersRef = useRef<MutationObserver[]>([]);

  const { t } = doTranslation(lng);

  const counts = Array.from({ length: 26 }, (_, index) => ({ value: `${index}`, label: `${index}` }));
  const wheelchairCounts = () => {
    const totalCount = props.adultCount + props.childCount + props.seniorCount;
    const limit = totalCount > 2 ? 2 : totalCount;
    return Array.from({ length: limit + 1 }, (_, index) => ({ value: `${index}`, label: `${index}` }));
  };
  const isMobile = useMediaQuery('(max-width: 600px)');

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        props.passengerOptions &&
        ref.current &&
        !ref.current.contains(event.target) &&
        event.target.getAttribute('role') != 'option'
      ) {
        props.setPassengerOptions(false);
      }
    };

    const handleKeydown = (event: any) => {
      if (event.key === 'Escape') {
        props.setPassengerOptions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('keydown', handleKeydown, true);
    };
  });

  useEffect(() => {
    observersRef.current.forEach((observer) => observer.disconnect());
    observersRef.current = [];

    const refs = [
      adultRef.current,
      seniorRef.current,
      childRef.current,
      ...(props.wheelchair ? [passengersRef.current] : [])
    ];

    const handleInputAriaDescribedby = (ref: { controlRef: HTMLElement; inputRef: HTMLInputElement }) => {
      if (ref && ref.controlRef && ref.inputRef) {
        const { controlRef, inputRef } = ref;
        inputRef.removeAttribute('aria-readonly');

        const handleClassChange = (mutationsList: MutationRecord[]) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              inputRef.removeAttribute('aria-readonly');
              inputRef.removeAttribute('aria-describedby');
            }
          }
        };

        const observer = new MutationObserver(handleClassChange);
        observer.observe(controlRef, { attributes: true });

        observersRef.current.push(observer);
      }
    };

    refs.forEach((ref) => handleInputAriaDescribedby(ref as any));

    return () => {
      observersRef.current.forEach((observer) => observer.disconnect());
    };
  }, [props.wheelchair]);

  const getPassengerText = (count: number, singularKey: string, pluralKey: string) => {
    const key = count <= 1 ? singularKey : pluralKey;
    return t(`passengers ${key}`, { count });
  };

  const buildPassengersSummaryText = () => {
    const passengers = [
      getPassengerText(props.adultCount, 'adults', 'adults_plural'),
      getPassengerText(props.seniorCount, 'seniors', 'seniors_plural'),
      getPassengerText(props.childCount, 'children', 'children_plural'),
      getPassengerText(props.wheelchairCount, 'wheelchairs', 'wheelchairs_plural')
    ];

    return t('You have selected {{ passengers }}', { passengers: passengers.join(', ') });
  };

  return (
    <div
      className={classNames({
        [styles.passengers]: true,
        [styles['trip-search']]: props.isTripSearch
      })}
      ref={ref}
    >
      <Select
        defaultValue={counts.filter((c) => c.value === props.adultCount.toString())}
        inputId="adult-passengers"
        instanceId="select-1"
        label={t('Adult')}
        name="adult"
        onChange={(c: any) => {
          props.setAdultCount(parseInt(c?.value));
        }}
        onFocus={(event) => {
          setTimeout(() => {
            event.target.setAttribute(
              'aria-describedby',
              'age-description-adult twentyfive-passengers react-select-select-1-live-region'
            );
          }, 500);
        }}
        options={counts}
        passengerType={'adult'}
        reference={adultRef}
        styles={{
          control: (baseStyles: any) => ({
            ...baseStyles,
            minWidth: isMobile
              ? 'calc(80rem / var(--font-factor)) !important'
              : 'calc(96rem / var(--font-factor)) !important'
          })
        }}
        subtitle={t('17 - 64 years')}
      />
      <div className={styles.line}></div>
      <Select
        defaultValue={counts.filter((c) => c.value === props.seniorCount.toString())}
        inputId="senior-passengers"
        instanceId="select-2"
        label={t('Senior')}
        name="senior"
        onChange={(c: any) => {
          props.setSeniorCount(parseInt(c?.value) || 0);
        }}
        onFocus={(event) => {
          setTimeout(() => {
            event.target.setAttribute(
              'aria-describedby',
              'age-description-senior twentyfive-passengers react-select-select-2-live-region'
            );
          }, 500);
        }}
        options={counts}
        passengerType={'senior'}
        reference={seniorRef}
        styles={{
          control: (baseStyles: any) => ({
            ...baseStyles,
            minWidth: isMobile
              ? 'calc(80rem / var(--font-factor)) !important'
              : 'calc(96rem / var(--font-factor)) !important'
          })
        }}
        subtitle={t('65+ years')}
      />
      <div className={styles.line}></div>
      <Select
        defaultValue={counts.filter((c) => c.value === props.childCount.toString())}
        inputId="child-passengers"
        instanceId="select-3"
        label={t('Child')}
        name="child"
        onChange={(c: any) => {
          props.setChildCount(parseInt(c?.value) || 0);
        }}
        onFocus={(event) => {
          setTimeout(() => {
            event.target.setAttribute(
              'aria-describedby',
              'age-description-child twentyfive-passengers react-select-select-3-live-region'
            );
          }, 500);
        }}
        options={counts}
        passengerType={'child'}
        reference={childRef}
        styles={{
          control: (baseStyles: any) => ({
            ...baseStyles,
            minWidth: isMobile
              ? 'calc(80rem / var(--font-factor)) !important'
              : 'calc(96rem / var(--font-factor)) !important'
          })
        }}
        subtitle={t('2 - 16 years')}
      />
      <div className={styles.line}></div>
      <div className={styles.wheelchair}>
        <div className={styles.heading}>
          <div className={styles.row}>
            <h2 className={styles.title}>{t('Wheelchair')}</h2>
            <div className={styles.info}>
              <Tooltip
                ariaLabelClose="wheelchair more info"
                ariaLabelOpen="close wheelchair more info"
                content={t(
                  'If you prefer to sit in a regular seat and store your wheelchair then you do not need to check the box.'
                )}
                id="wheelchair-tooltip"
                place="top"
                wrap={true}
              >
                <Image alt={t('Info')} height="16" src={circleInfoDark} width="16" />
              </Tooltip>
            </div>
          </div>
          <span className={styles.subtitle}>{t('Passengers traveling in a wheelchair')}</span>
        </div>
        <button
          aria-label={t('passengers traveling in a wheelchair')}
          className={styles.info}
          type="button"
          id="toggle"
          role="switch"
          aria-checked={props.wheelchair}
          onClick={() => {
            if (props.wheelchair && props.wheelchairCount > 0) {
              props.setWheelchairCount(0);
            }
            props.setWheelchair(!props.wheelchair);
          }}
        >
          {props.wheelchairCount > 0 ? (
            <Image alt={t('toggle')} height="31" src={toggleOn} width="51" />
          ) : props.wheelchair ? (
            <Image alt={t('toggle')} height="31" src={toggleOn} width="51" />
          ) : (
            <Image alt={t('toggle')} height="31" src={toggleOff} width="51" />
          )}
        </button>
      </div>
      {props.wheelchair && (
        <>
          <div className={styles.line}></div>
          <Select
            defaultValue={wheelchairCounts().filter((c) => c.value === props.wheelchairCount.toString())}
            inputId="number-wheelchair-passengers"
            instanceId="select-4"
            label={t('Passengers')}
            name="passengers"
            onChange={(c: any) => {
              const count = parseInt(c?.value) || 0;
              props.setWheelchairCount(count);
            }}
            onFocus={(event) => {
              setTimeout(() => {
                event.target.setAttribute(
                  'aria-describedby',
                  'two-wheelchair-passengers-info react-select-select-4-live-region'
                );
              }, 500);
            }}
            options={wheelchairCounts()}
            passengerType={'passengers'}
            reference={passengersRef}
            styles={{
              control: (baseStyles: any) => ({
                ...baseStyles,
                minWidth: isMobile
                  ? 'calc(80rem / var(--font-factor)) !important'
                  : 'calc(96rem / var(--font-factor)) !important'
              })
            }}
          />
          <div className={styles.information} id="two-wheelchair-passengers-info">
            <div className={styles.info}>
              <Image alt={t('Info')} height="18" src={circleInfo} width="18" />
            </div>
            <p className={styles.text}>{t('Limit of 2 wheelchair passengers per trip.')}</p>
          </div>
        </>
      )}
      <div className={styles.information} id="twentyfive-passengers">
        <div className={styles.info}>
          <Image alt={t('Info')} height="18" src={circleInfo} width="18" />
        </div>
        <p className={styles.text}>
          {t(
            'Limit of 25 passengers per trip. To book tickets for more than 25 passengers, please complete additional bookings.'
          )}
        </p>
      </div>
      <div className={styles.save}>
        <ButtonTertiary
          dark
          onClick={() => {
            if (props.setPassengerOptions) {
              props.setPassengerOptions(!props.passengerOptions);
            }
          }}
        >
          <span className="sr-only">{buildPassengersSummaryText()}</span>
          {t('Close')}
        </ButtonTertiary>
      </div>
    </div>
  );
};

export default Passengers;
